import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, waLink } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Facebook, Twitter, Linkedin, Share2, User, Clock, Calendar as CalIcon,
  MessageCircle, ArrowRight, List,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../lib/Seo";

/* Extract h2/h3 headings from HTML content and inject anchors */
function useTocContent(html) {
  return useMemo(() => {
    if (!html) return { html: "", toc: [] };
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const headings = doc.querySelectorAll("h2, h3");
    const toc = [];
    headings.forEach((h, i) => {
      const text = h.textContent || `Section ${i+1}`;
      const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60) || `section-${i}`;
      h.setAttribute("id", id);
      toc.push({ id, text, level: h.tagName === "H2" ? 2 : 3 });
    });
    return { html: doc.body.firstElementChild?.innerHTML || html, toc };
  }, [html]);
}

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => {
      setPost(r.data);
      api.get(`/blog?category=${encodeURIComponent(r.data.category)}`).then((rr) =>
        setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 3))
      );
    });
  }, [slug]);

  const { html: contentHtml, toc } = useTocContent(post?.content);

  if (!post) return <div className="min-h-[60vh] grid place-items-center text-foreground/60">Loading...</div>;

  const url = window.location.href;
  const share = (kind) => {
    const links = {
      facebook: `https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`,
      linkedin: `https://linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: waLink(`${post.title} — ${url}`),
    };
    if (kind === "copy") {
      navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } else {
      window.open(links[kind], "_blank", "width=600,height=500");
    }
  };

  // JSON-LD schema (Article)
  const ldJson = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.created_at,
    author: { "@type": "Person", name: post.author || "Bhupaal Bisht" },
    publisher: { "@type": "Organization", name: "YourAstroHub" },
    mainEntityOfPage: url,
    keywords: (post.tags || []).join(", "),
  };

  return (
    <div data-testid="blog-detail-page" className="min-h-screen">
      <Seo
        title={post.meta_title || `${post.title} | YourAstroHub Blog`}
        description={post.meta_description || post.excerpt}
        keywords={(post.tags || []).join(", ")}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.category, path: `/blog?category=${encodeURIComponent(post.category)}` },
        ]}
        schemas={[ldJson]}
      />
      <article className="max-w-6xl mx-auto px-6 md:px-8 pt-6 pb-16">
        {/* Breadcrumbs */}
        <nav aria-label="breadcrumb" className="text-[11px] tracking-[0.22em] uppercase text-foreground/60 mb-6">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
          <span className="mx-2 text-[#D4AF37]">/</span>
          <Link to="/blog" className="hover:text-[#D4AF37]">Blog</Link>
          <span className="mx-2 text-[#D4AF37]">/</span>
          <span className="text-[#D4AF37]">{post.category}</span>
        </nav>

        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-[#D4AF37] mb-6"><ArrowLeft className="w-4 h-4" />All articles</Link>

        <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">{post.category}</div>
        <h1 className="font-serif text-4xl md:text-6xl font-light leading-[1.05] mb-6">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground/60 mb-8">
          <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{post.author || "Bhupaal Bisht"}</span>
          <span className="flex items-center gap-1.5"><CalIcon className="w-3.5 h-3.5" />{fmtDate(post.created_at)}</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{post.reading_time || 5} min read</span>
        </div>

        <div className="rounded-3xl overflow-hidden mb-10 aspect-[16/9]">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="grid md:grid-cols-12 gap-10">
          {/* Sticky TOC */}
          <aside className="md:col-span-3 hidden md:block">
            {toc.length > 0 && (
              <div className="sticky top-28 rounded-3xl border border-foreground/10 p-5" data-testid="blog-toc">
                <div className="eyebrow mb-4 flex items-center gap-2"><List className="w-3 h-3" />On this page</div>
                <ul className="space-y-2 text-sm">
                  {toc.map((t) => (
                    <li key={t.id} className={t.level === 3 ? "pl-4" : ""}>
                      <a href={`#${t.id}`} className="text-foreground/70 hover:text-[#D4AF37] transition-colors">{t.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>

          {/* Content */}
          <div className="md:col-span-9">
            <div className="astro-prose max-w-3xl" dangerouslySetInnerHTML={{ __html: contentHtml }} />

            {/* Tags */}
            {(post.tags || []).length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs px-3 py-1.5 rounded-full border border-foreground/15 text-foreground/70">#{t}</span>
                ))}
              </div>
            )}

            {/* Share */}
            <div className="mt-10 border-t border-foreground/10 pt-6 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-foreground/70">Share this article</div>
              <div className="flex gap-2">
                <button data-testid="share-facebook" onClick={() => share("facebook")} className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><Facebook className="w-4 h-4" /></button>
                <button data-testid="share-twitter" onClick={() => share("twitter")} className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><Twitter className="w-4 h-4" /></button>
                <button data-testid="share-linkedin" onClick={() => share("linkedin")} className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><Linkedin className="w-4 h-4" /></button>
                <button data-testid="share-whatsapp" onClick={() => share("whatsapp")} className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#25D366] hover:text-[#25D366] grid place-items-center"><MessageCircle className="w-4 h-4" /></button>
                <button data-testid="share-copy" onClick={() => share("copy")} className="w-10 h-10 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center"><Share2 className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Author card */}
            <div className="mt-10 p-6 md:p-8 rounded-3xl border border-[#D4AF37]/30 bg-[#0B1F3A] text-[#F5E8D8] flex items-center gap-5">
              <img src="/assets/bhupaal-bisht.jpg" alt="Bhupaal Bisht" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover ring-2 ring-[#D4AF37] shrink-0" />
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">Author</div>
                <div className="font-serif text-2xl mt-1">Bhupaal Bisht</div>
                <div className="text-xs text-[#F5E8D8]/70 mt-1">Vedic Astrologer · Astrology Mentor · Founder of YourAstroHub</div>
              </div>
              <Link to="/consultations" className="hidden sm:inline-block">
                <Button size="sm" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold">Book Session</Button>
              </Link>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="pb-24 bg-[#F4EFE6] dark:bg-[#0F284B] py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="font-serif text-3xl md:text-4xl font-light mb-8">Related articles</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-3xl overflow-hidden bg-background border border-foreground/10 hover:border-[#D4AF37]/50 transition-all hover:-translate-y-1">
                  <div className="aspect-[16/10] overflow-hidden"><img src={p.cover_image} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-2">{p.category}</div>
                    <h3 className="font-serif text-lg leading-tight group-hover:text-[#D4AF37] transition-colors">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
